// src/BostaPage.js
import React, { useState, useEffect } from 'react';
import { onAuthStateChange } from './lib/supabaseAuth';
import { subscribeMyBrand } from './lib/confirmationData';
import { callSaveBostaApiKey } from './firebase';
import { useNavigate } from 'react-router-dom';
import { useI18n } from './i18n';
import './BostaPage.css';

function BostaPage() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();

  const [currentUser, setCurrentUser] = useState(null);
  const [clientInfo, setClientInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  // بيانات الفورم
  const [bostaApiKey, setBostaApiKey] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  // 🔒 قفل ثغرة uid-in-URL (Wave 3b): مفيش أي `?uid=`/`?admin=` كمصدر
  // هوية بعد كده — البراند بيتجاب دايمًا من الجلسة الحالية (getMyBrand()،
  // RLS بتفرض auth_uid=auth.uid() تلقائيًا). زائر مش مسجّل دخول يترحّل
  // لصفحة اللوجن، مش يدخل بـ uid في الرابط.
  useEffect(() => {
    const unsubscribe = onAuthStateChange((user) => {
      setCurrentUser(user);
      if (!user) {
        navigate('/');
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    if (!currentUser) return undefined;
    const unsubscribe = subscribeMyBrand(({ brand, error }) => {
      if (error) {
        console.error('Error fetching client data:', error);
        setMessage(t('bosta.page.loadError'));
        setLoading(false);
        return;
      }
      if (!brand) {
        setMessage(t('bosta.page.clientNotFound'));
        setLoading(false);
        return;
      }
      setClientInfo(brand);
      // ⚠️ `bosta_api` هو سرّ مستبعد عمدًا من whitelist `get_my_brand_settings()`
      // (daf_confirmation_rls.sql) — لا يوجد أي طريق آمن لقراءته من المتصفح.
      // الخانة بتفضل فاضية دايمًا (التاجر يقدر يكتب فوقها بس مش يشوف القيمة
      // القديمة تاني) — نفس مبدأ خانة الباسورد، مش تسريع/باگ.
      // ✅ Wave 3b cleanup (2026-07-05): `brand.has_bosta_api_key` بوليان
      // وجود-فقط جديد من نفس الـRPC (أبدًا القيمة الخام) — بيدّي التاجر
      // إشارة "متحفوظ فعلاً" بدل ما يفضل مش عارف لو المفتاح متحطّ قبل كده.
      setLoading(false);
    });
    return () => unsubscribe();
  }, [currentUser, t]);

  const handleSave = async () => {
    if (!(bostaApiKey || '').trim()) {
      setMessage(t('bosta.page.apiKeyRequired'));
      return;
    }

    setSaving(true);
    setMessage('');

    try {
      // ⚠️ `bosta_api` سرّ — مفيش RPC عليه (شوف MIGRATION_MAP.md §6). الحفظ
      // بيعدّي عبر راوت سيرفر مخصّص (service_role)، مش update مباشر.
      await callSaveBostaApiKey({
        brand: clientInfo.brand_name,
        bostaApiKey: (bostaApiKey || '').trim(),
      });

      // تحديث فوري (optimistic) لبوليان "متحفوظ فعلاً" — مش لازم ننتظر
      // إعادة الفتش عبر subscribeMyBrand()/Realtime.
      setClientInfo((prev) => (prev ? { ...prev, has_bosta_api_key: true } : prev));

      setMessage(t('bosta.page.saveSuccess'));
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error saving API key:', error);
      setMessage(t('bosta.page.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleCopyWebhook = () => {
    const webhookUrl = `https://n8n2.dafaiagency.shop/webhook/65b873aa-5c25-4a75-a3f7-10b07835a9c9/bosta-hook-alsayes3/${clientInfo.webhook_path_id}`;
    navigator.clipboard.writeText(webhookUrl);
    setMessage(t('bosta.page.webhookCopied'));
    setTimeout(() => setMessage(''), 2000);
  };

  if (loading) {
    return (
      <div className="bosta-container">
        <div className="loading">{t('loading.app')}</div>
      </div>
    );
  }

  if (!clientInfo) {
    return (
      <div className="bosta-container">
        <div className="error-message">{t('bosta.page.clientNotFound')}</div>
      </div>
    );
  }

  const webhookUrl = `https://n8n2.dafaiagency.shop/webhook/65b873aa-5c25-4a75-a3f7-10b07835a9c9/bosta-hook-alsayes3/${clientInfo.webhook_path_id}`;

  return (
    <div className="bosta-container" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
      <div className="bosta-card">
        {/* Header */}
        <button
          // 🔒 مش بنمرّر location.search هنا لأنه كان أساسًا لتمرير
          // ?uid=/?admin= (uid-in-URL) — الدخول للداشبورد دلوقتي عبر
          // الجلسة بس، صفر باراميتر هوية في التنقل.
          onClick={() => navigate('/dashboard')}
          className="back-button"
        >
          {lang === 'ar' ? '→' : '←'} {t('common.back') || (lang === 'ar' ? 'رجوع' : 'Back')}
        </button>
        <div className="bosta-header">
          <h1 className="bosta-title">{t('bosta.page.title')}</h1>
        </div>

        {/* Webhook Section */}
        <div className="section">
          <h2 className="section-title">{t('bosta.page.webhookTitle')}</h2>
          <p className="section-description">
            {t('bosta.page.webhookDescription')}
          </p>
          
          <div className="webhook-box">
            <code className="webhook-url">{webhookUrl}</code>
          </div>

          <button 
            onClick={handleCopyWebhook}
            className="copy-button"
          >
            {t('bosta.page.copyLink')}
          </button>
        </div>

        {/* API Key Section */}
        <div className="section">
          <h2 className="section-title">{t('bosta.page.apiKeyTitle')}</h2>
          <p className="section-description">
            {t('bosta.page.apiKeyDescription')}
          </p>

          {/* ⚠️ بوليان وجود-فقط (`has_bosta_api_key`) — أبدًا القيمة الخام
              نفسها. الخانة تحت بتفضل فاضية دايمًا (مش تعرض السرّ القديم)،
              الشارة دي بس بتقول للتاجر "متحفوظ فعلاً" ولا لأ. */}
          <div
            className={`key-status ${clientInfo.has_bosta_api_key ? 'key-status--configured' : 'key-status--missing'}`}
          >
            {clientInfo.has_bosta_api_key ? t('bosta.page.keyConfigured') : t('bosta.page.keyNotConfigured')}
          </div>

          <input
            type="text"
            value={bostaApiKey}
            onChange={(e) => setBostaApiKey(e.target.value)}
            placeholder={t('bosta.page.apiKeyPlaceholder')}
            className="api-input"
            disabled={saving}
          />

          <button 
            onClick={handleSave}
            disabled={saving || !(bostaApiKey || '').trim()}
            className="save-button"
          >
            {saving ? t('bosta.page.saving') : t('bosta.page.save')}
          </button>
        </div>

        {/* Message */}
        {message && (
          <div className={`message ${message.includes('✅') ? 'success' : message.includes('❌') ? 'error' : 'warning'}`}>
            {message}
          </div>
        )}

        {/* Info */}
        <div className="info-box">
          <p><strong>{t('bosta.page.noteTitle')}</strong></p>
          <p>• {t('bosta.page.note1')}</p>
          <p>• {t('bosta.page.note2')}</p>
          <p>• {t('bosta.page.note3')}</p>
        </div>
      </div>
    </div>
  );
}

export default BostaPage;
